<template>
  <div class="calculator-view font-pixel select-none max-w-[260px] mx-auto">
    <div
      class="border-2 border-black bg-gray-200 px-3 py-2 text-right text-3xl text-black min-h-[3rem] flex items-center justify-end break-all"
      aria-live="polite"
    >
      {{ display }}
    </div>
    <div class="grid grid-cols-4 gap-1 mt-2">
      <button
        v-for="(key, idx) in keys"
        :key="`${key.label}-${idx}`"
        type="button"
        class="border-2 border-black py-2 text-3xl font-medium text-black bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors cursor-pointer"
        :class="[
          key.wide ? 'col-span-2' : '',
          key.action === 'noop' ? 'pointer-events-none border-transparent bg-transparent hover:bg-transparent active:bg-transparent' : '',
        ]"
        :disabled="key.action === 'noop'"
        @click="onKey(key)"
      >
        {{ key.label }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const display = ref('0')
/** @type {import('vue').Ref<number | null>} */
const stored = ref(null)
/** @type {import('vue').Ref<string | null>} */
const pendingOp = ref(null)
const newOperand = ref(true)

/** @type {Array<{ label: string, action: string, op?: string, d?: string, wide?: boolean }>} */
const keys = [
  { label: 'C', action: 'clear' },
  { label: '÷', action: 'op', op: '/' },
  { label: '×', action: 'op', op: '*' },
  { label: '⌫', action: 'back' },
  { label: '7', action: 'digit', d: '7' },
  { label: '8', action: 'digit', d: '8' },
  { label: '9', action: 'digit', d: '9' },
  { label: '−', action: 'op', op: '-' },
  { label: '4', action: 'digit', d: '4' },
  { label: '5', action: 'digit', d: '5' },
  { label: '6', action: 'digit', d: '6' },
  { label: '+', action: 'op', op: '+' },
  { label: '1', action: 'digit', d: '1' },
  { label: '2', action: 'digit', d: '2' },
  { label: '3', action: 'digit', d: '3' },
  { label: ' ', action: 'noop' },
  { label: '0', action: 'digit', d: '0', wide: true },
  { label: '.', action: 'dot' },
  { label: '=', action: 'eq' },
]

function format(n) {
  if (!Number.isFinite(n)) return 'Error'
  const s = String(n)
  if (s.length > 14) return n.toPrecision(10)
  return s
}

function apply(a, b, op) {
  switch (op) {
    case '+':
      return a + b
    case '-':
      return a - b
    case '*':
      return a * b
    case '/':
      return b === 0 ? NaN : a / b
    default:
      return b
  }
}

function flushPending() {
  if (stored.value === null || pendingOp.value === null) return
  const cur = parseFloat(display.value)
  if (Number.isNaN(cur)) return
  const result = apply(stored.value, cur, pendingOp.value)
  display.value = format(result)
  stored.value = Number.isFinite(result) ? result : null
}

function onKey(key) {
  switch (key.action) {
    case 'clear':
      display.value = '0'
      stored.value = null
      pendingOp.value = null
      newOperand.value = true
      break
    case 'back':
      if (newOperand.value) break
      if (display.value.length <= 1) display.value = '0'
      else display.value = display.value.slice(0, -1)
      break
    case 'digit': {
      const d = key.d
      if (!d) break
      if (newOperand.value) {
        display.value = d === '0' ? '0' : d
        newOperand.value = false
      } else if (display.value === '0' && d !== '0') {
        display.value = d
      } else if (display.value !== '0' || d === '0') {
        display.value += d
      }
      break
    }
    case 'dot':
      if (newOperand.value) {
        display.value = '0.'
        newOperand.value = false
      } else if (!display.value.includes('.')) {
        display.value += '.'
      }
      break
    case 'op': {
      const cur = parseFloat(display.value)
      if (Number.isNaN(cur)) break
      if (stored.value !== null && pendingOp.value && !newOperand.value) {
        flushPending()
      } else {
        stored.value = cur
      }
      pendingOp.value = key.op ?? null
      newOperand.value = true
      break
    }
    case 'eq':
      if (stored.value !== null && pendingOp.value && !newOperand.value) {
        flushPending()
        pendingOp.value = null
        newOperand.value = true
      }
      break
    case 'noop':
      break
    default:
      break
  }
}
</script>
